import {useLayoutEffect,useState,type ImgHTMLAttributes} from 'react';
import type {Asset} from '../shared/types';
import {ArtworkImageCache,ArtworkImageError} from './artworkImageCache';

/** Personal previews lease bytes only while their current catalog permits access. */
export default function ArtworkThumbnail({asset,cache,onUnavailable,...props}:Omit<ImgHTMLAttributes<HTMLImageElement>,'src'|'alt'>&{asset:Asset;cache?:ArtworkImageCache;onUnavailable?:()=>void}){
 const [loaded,setLoaded]=useState<{cache:ArtworkImageCache;source:string;href:string|undefined}|null>(null);
 const [decoded,setDecoded]=useState<string|null>(null);
 const allowed=!cache||cache.canRead(asset.url);
 useLayoutEffect(()=>{
  if(!cache||!allowed)return;
  let active=true;const lease=cache.acquire(asset.url,asset.width,asset.height);
  setLoaded({cache,source:asset.url,href:lease.href});
  void lease.promise.then(href=>{if(active)setLoaded({cache,source:asset.url,href});}).catch(error=>{
   if(active&&error instanceof ArtworkImageError&&[401,403,410].includes(error.status??0))onUnavailable?.();
  });
  return()=>{active=false;lease.release();};
 },[cache,asset.url,asset.width,asset.height,allowed,onUnavailable]);
 const href=!allowed?undefined:!cache?asset.url:loaded?.cache===cache&&loaded.source===asset.url?loaded.href:cache.peek(asset.url);
 // Keep its space and accessible label, but never paint a missing/failed image
 // icon while private bytes or their decoder are still unavailable.
 return <img {...props} src={href} alt={asset.label} style={{...props.style,opacity:href&&decoded===href?props.style?.opacity:0}}
  onLoad={event=>{
   const image=event.currentTarget,source=image.currentSrc;
   props.onLoad?.(event);
   void image.decode().then(()=>{if(image.isConnected&&image.currentSrc===source)setDecoded(href??null);}).catch(()=>{if(image.isConnected&&image.currentSrc===source)setDecoded(null);});
  }}
  onError={event=>{setDecoded(null);props.onError?.(event);}}/>;
}
